import { get, set } from 'lodash';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { ObjectKeys } from '@/lib/utils';
import type { Filter, FilterEnumChoice } from './constants';

/**
 * Handles removing a filter choice from the filter copy
 */
export const handleRemoveFilter = ({
  choice,
  filterCopy,
  filterVariant,
}: {
  choice: FilterEnumChoice;
  filterCopy: JsonFilter;
  filterVariant: Filter;
}): void => {
  const externalFieldName =
    choice.overrideExternalFieldName ?? filterVariant.externalFieldName;
  choice.filters.forEach((f) => {
    const prevValue = get(filterCopy, `${externalFieldName}.${f.operator}`);

    if (Array.isArray(prevValue)) {
      const filtered = (prevValue ?? []).filter((p) => p != f.value);

      if (filtered.length) {
        set(filterCopy, `${externalFieldName}.${f.operator}`, filtered);
      } else {
        if (filterCopy[externalFieldName]?.[f.operator])
          delete filterCopy[externalFieldName][f.operator];

        // clean filter if no more operators for external field name
        if (ObjectKeys(filterCopy[externalFieldName] ?? {}).length === 0) {
          delete filterCopy[externalFieldName];
        }
      }
    }
  });
};

/**
 * Handles adding a filter choice to the filter copy
 */
export const handleAddFilter = ({
  choice,
  filterCopy,
  filterVariant,
}: {
  choice: FilterEnumChoice;
  filterCopy: JsonFilter;
  filterVariant: Filter;
}): void => {
  choice.filters.forEach((f) => {
    const externalFieldName =
      f.fieldName ??
      choice.overrideExternalFieldName ??
      filterVariant.externalFieldName;

    const prevValue = get(filterCopy, `${externalFieldName}.${f.operator}`);
    set(filterCopy, `${externalFieldName}.${f.operator}`, [
      ...(prevValue && Array.isArray(prevValue) ? prevValue : []),
      f.value,
    ]);
  });
};

/**
 * Checks if a filter choice is currently active
 */
export const getFilterIsActive = (
  filterVariant: Filter,
  choice: FilterEnumChoice,
  filter: JsonFilter | null
): boolean => {
  const choiceIsSelected = choice.filters.every((cur) => {
    const externalFieldName =
      cur.fieldName ??
      choice.overrideExternalFieldName ??
      filterVariant.externalFieldName;

    const current = externalFieldName ? filter?.[externalFieldName] : undefined;
    if (!current) return false;
    const currentVal = get(current, cur.operator);
    return Array.isArray(currentVal) && currentVal.includes(cur.value);
  });

  return choiceIsSelected;
};
